'use client'

import { createContext, useMemo, useState } from 'react'
import { toast } from '@/lib/toast'
import { isCustomerAuthActive } from '@/utils/util'
import { toggleWishlistApi } from '@/lib/commerce/services/wishlist-api'
import type { WishlistContextValue, Wishlist } from '@/lib/commerce/types/wishlist'
import { useAuth } from '@/lib/commerce/hooks/useAuth'

export const WishlistContext = createContext<WishlistContextValue | null>(null)

const buildVariantSet = (wishlist: Wishlist) => {
  const set = new Set<string>()
  if (!wishlist?.products) return set
  for (const p of wishlist.products) {
    if (p.variant?.id) set.add(`${p.id}:${p.variant.id}`)
    set.add(p.id)
  }
  return set
}

export function WishlistProvider({ initialWishlist, children }: { initialWishlist: Wishlist, children: React.ReactNode }) {
  const { session, vendor } = useAuth()
  const [entries, setEntries] = useState<Set<string>>(() => buildVariantSet(initialWishlist))
  const [loading, setLoading] = useState(false)

  const productIds = useMemo(() => {
    const ids: string[] = []
    for (const key of entries) if (!key.includes(':')) ids.push(key)
    return ids
  }, [entries])

  const isInWishlist = (productId: string, variantId?: string) => {
    if (variantId) return entries.has(`${productId}:${variantId}`)
    return entries.has(productId)
  }

  const toggle = async ({ productId, variantId }: { productId: string; variantId: string }) => {
    if (!isCustomerAuthActive(vendor)) return
    if (!session?.user?.id) {
      toast.error('Та нэвтэрч орсны дараагаар хүслийн жагсаалтанд нэмэх боломжтой')
      return
    }
    const variantKey = `${productId}:${variantId}`
    const wasIn = entries.has(variantKey)
    const next = new Set(entries)
    if (wasIn) {
      next.delete(variantKey)
      const stillHasAnyVariant = Array.from(next).some(k => k.startsWith(`${productId}:`))
      if (!stillHasAnyVariant) next.delete(productId)
    } else {
      next.add(variantKey)
      next.add(productId)
    }
    setEntries(next)
    setLoading(true)
    try {
      const res = await toggleWishlistApi(vendor?.id, productId, variantId, session)
      if (!res?.status || res.status !== 200) {
        setEntries(entries)
        toast.error('Хүслийн жагсаалт шинэчлэхэд алдаа гарлаа')
        return
      }
      toast.success(wasIn ? 'Хүслийн жагсаалтаас хасагдлаа' : 'Хүслийн жагсаалтад нэмэгдлээ')
    } catch (error) {
      console.error('Error updating wishlist:', error)
      setEntries(entries)
      toast.error('Хүслийн жагсаалт шинэчлэхэд алдаа гарлаа')
    } finally {
      setLoading(false)
    }
  }

  return (
    <WishlistContext.Provider value={{ productIds, loading, isInWishlist, toggle }}>
      {children}
    </WishlistContext.Provider>
  )
}

export default WishlistProvider
